/**
 * Application Constants
 *
 * Centralized location for all magic numbers, strings, and configuration
 */

// ============= BOOKING STATUS =============
export const BOOKING_STATUS = {
  SCHEDULED: 'scheduled',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  RESCHEDULED: 'rescheduled',
  ON_THE_WAY: 'on_the_way',
  PENDING: 'pending',
} as const;

export type BookingStatus = (typeof BOOKING_STATUS)[keyof typeof BOOKING_STATUS];

// ============= PRICING CONSTANTS =============
export const PRICING = {
  // Transportation
  TRANSPORTATION_FEE: 1500,
  TRANSPORTATION_DISTANCE_KM: 30,

  // Allowances
  NIGHT_ALLOWANCE: 1500,
  NIGHT_START_HOUR: 20,
  NIGHT_END_HOUR: 8,

  // Cancellation Policy
  CANCELLATION_FREE_DAYS: 10,
  CANCELLATION_HALF_REFUND_DAYS: 5,
  CANCELLATION_FULL_CHARGE_DAYS: 3,

  // Add-ons
  ADDON_MULTIPLIER: 1.0,
} as const;

// ============= LOYALTY TIERS =============
export const LOYALTY_TIERS = {
  BRONZE: {
    name: 'Bronze',
    multiplier: 1,
    minSpending: 0,
    maxSpending: 49999,
    color: '#CD7F32',
    benefits: ['Earn 1 point per 100 spent', 'Basic support'],
  },
  SILVER: {
    name: 'Silver',
    multiplier: 1.25,
    minSpending: 50000,
    maxSpending: 99999,
    color: '#C0C0C0',
    benefits: ['Earn 1.25 points per 100 spent', 'Priority support', '5% discount on services'],
  },
  GOLD: {
    name: 'Gold',
    multiplier: 1.5,
    minSpending: 100000,
    maxSpending: 249999,
    color: '#FFD700',
    benefits: ['Earn 1.5 points per 100 spent', 'Premium support', '10% discount', 'Free rescheduling'],
  },
  PLATINUM: {
    name: 'Platinum',
    multiplier: 2,
    minSpending: 250000,
    maxSpending: Infinity,
    color: '#E5E4E2',
    benefits: ['Earn 2 points per 100 spent', 'VIP support', '15% discount', 'Free cancellation'],
  },
} as const;

export const LOYALTY_POINTS_PER_100 = 1;
export const POINTS_REDEMPTION_VALUE = 1; // 1 point = 1 currency unit at redemption

// ============= SERVICE TYPES =============
export const SERVICE_TYPES = {
  DEEP_CLEANING: 'deep-cleaning',
  RESIDENTIAL: 'residential',
  COMMERCIAL: 'commercial',
  SOFA_CLEANING: 'sofa-cleaning',
  BATHROOM_CLEANING: 'bathroom-cleaning',
  KITCHEN_CLEANING: 'kitchen-cleaning',
  WATER_TANK: 'water-tank',
  CUSTOM: 'custom',
} as const;

export type ServiceType = (typeof SERVICE_TYPES)[keyof typeof SERVICE_TYPES];

// ============= PROPERTY TYPES =============
export const PROPERTY_TYPES = {
  RESIDENTIAL: {
    '300-750': '1 BHK',
    '750-1200': '2 BHK',
    '1200-1800': '3 BHK',
    '1800-2500': '4 BHK',
    '2500+': '5+ BHK',
  },
  COMMERCIAL: {
    '0-1000': 'Small Office',
    '1000-2500': 'Medium Office',
    '2500-5000': 'Large Office',
    '5000+': 'Corporate',
  },
} as const;

// ============= FREQUENCY OPTIONS =============
export const FREQUENCY = {
  ONE_TIME: 'one-time',
  WEEKLY: 'weekly',
  BI_WEEKLY: 'bi-weekly',
  MONTHLY: 'monthly',
  CUSTOM: 'custom',
} as const;

export type FrequencyType = (typeof FREQUENCY)[keyof typeof FREQUENCY];

// ============= PAYMENT STATUS =============
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  PARTIAL: 'partial',
  FAILED: 'failed',
  REFUNDED: 'refunded',
} as const;

export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

// ============= PAYMENT METHODS =============
export const PAYMENT_METHODS = {
  CASH: 'cash',
  CARD: 'card',
  UPI: 'upi',
  BANK_TRANSFER: 'bank_transfer',
  ONLINE: 'online',
} as const;

export type PaymentMethod = (typeof PAYMENT_METHODS)[keyof typeof PAYMENT_METHODS];

// ============= USER ROLES =============
export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  SUPERVISOR: 'supervisor',
  EMPLOYEE: 'employee',
  CUSTOMER: 'customer',
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// ============= ATTENDANCE STATUS =============
export const ATTENDANCE_STATUS = {
  PRESENT: 'present',
  ABSENT: 'absent',
  HALF_DAY: 'half_day',
  PAID_LEAVE: 'paid_leave',
  SICK_LEAVE: 'sick_leave',
  HOLIDAY: 'holiday',
  WEEK_OFF: 'week_off',
} as const;

export type AttendanceStatus = (typeof ATTENDANCE_STATUS)[keyof typeof ATTENDANCE_STATUS];

// ============= WORK HOURS =============
export const WORK_HOURS = {
  STANDARD_HOURS_PER_DAY: 9,
  OVERTIME_START_HOUR: 9,
  OVERTIME_MULTIPLIER: 1.0,
  WEEKLY_BONUS_HOURS: 54,
  WEEKLY_BONUS_AMOUNT: 1000,
} as const;

// ============= ENQUIRY STATUS =============
export const ENQUIRY_STATUS = {
  NEW: 'new',
  CONTACTED: 'contacted',
  QUOTED: 'quoted',
  WAITING_CONFIRMATION: 'waiting-confirmation',
  CONFIRMED: 'confirmed',
  CONVERTED: 'converted',
  LOST: 'lost',
  FOLLOW_UP_REQUIRED: 'follow-up-required',
} as const;

export type EnquiryStatus = (typeof ENQUIRY_STATUS)[keyof typeof ENQUIRY_STATUS];

// ============= ENQUIRY SOURCES =============
export const ENQUIRY_SOURCES = {
  PHONE: 'phone',
  WHATSAPP: 'whatsapp',
  WEBSITE: 'website',
  REFERRAL: 'referral',
  WALK_IN: 'walk-in',
  SOCIAL_MEDIA: 'social-media',
} as const;

export type EnquirySource = (typeof ENQUIRY_SOURCES)[keyof typeof ENQUIRY_SOURCES];

// ============= PRIORITY LEVELS =============
export const PRIORITY_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent',
} as const;

export type PriorityLevel = (typeof PRIORITY_LEVELS)[keyof typeof PRIORITY_LEVELS];

// ============= DATE/TIME FORMATS =============
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  INPUT: 'yyyy-MM-dd',
  FULL: 'MMMM dd, yyyy',
  SHORT: 'MM/dd/yy',
} as const;

export const TIME_FORMATS = {
  DISPLAY_12: 'h:mm a',
  DISPLAY_24: 'HH:mm',
  INPUT: 'HH:mm',
} as const;

// ============= VALIDATION RULES =============
export const VALIDATION = {
  PHONE_MIN_LENGTH: 10,
  PHONE_MAX_LENGTH: 15,
  PASSWORD_MIN_LENGTH: 8,
  OTP_LENGTH: 4,
  MIN_BOOKING_DAYS_ADVANCE: 0,
  MAX_BOOKING_DAYS_ADVANCE: 90,
  MAX_FILE_SIZE_MB: 5,
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp'],
  ALLOWED_DOCUMENT_TYPES: ['application/pdf', 'image/jpeg', 'image/png'],
} as const;

// ============= PAGINATION =============
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  MOBILE_PAGE_SIZE: 10,
} as const;

// ============= API TIMEOUTS =============
export const TIMEOUTS = {
  DEFAULT: 30000, // 30 seconds
  UPLOAD: 60000, // 60 seconds
  LONG_RUNNING: 120000, // 2 minutes
} as const;

// ============= LOCAL STORAGE KEYS =============
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_PREFERENCES: 'user_preferences',
  THEME: 'theme',
  LANGUAGE: 'language',
  RECENT_SEARCHES: 'recent_searches',
  DRAFT_BOOKING: 'draft_booking',
} as const;

// ============= BREAKPOINTS (for responsive design) =============
export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  '2XL': 1536,
} as const;

// ============= COMPANY INFO =============
export const COMPANY = {
  NAME: 'Tools & Bubbles',
  LEGAL_NAME: 'Tools and Bubbles Cleaning Service Kochi (OPC) Private Limited',
  PHONE: '+919995216660',
  EMAIL: 'customercare@toolsandbubbles.com',
  ADDRESS: 'WS VO/135, 1ST FLOOR, CC 54, 2593-5, BOSE NAGAR, KADAVANTHARA, Kochi, Kerala, 682020',
  /** Kadavanthara office — used for map pin (Embed API place_id alone can offset the marker) */
  MAP_LAT: 9.971638126451644,
  MAP_LNG: 76.30239967603276,
  ESTABLISHED_YEAR: 2009,
  /** Opens the verified Google Business listing (same as public llms.txt) */
  GOOGLE_MAPS_URL: 'https://www.google.com/maps?cid=6166677786164860320',
  GOOGLE_BUSINESS_PROFILE_ID: '6166677786164860320',
  GOOGLE_PLACE_ID: 'ChIJ4UXdCssNCDsR1QePI2P7M-Y', // Tools & Bubbles verified place ID
  GOOGLE_REVIEW_LINK: 'https://g.page/r/CdUHjyNj-zPmEAE/review',
  GOOGLE_REVIEW_SHORT_LINK: 'https://bit.ly/tools-bubbles-review',
  JUSTDIAL_LINK: 'https://www.justdial.com/Kochi/Tools-Bubbles',
  FACEBOOK_PAGE: 'https://facebook.com/toolsandbubbles',
  INSTAGRAM_PAGE: 'https://instagram.com/toolsandbubbles',
} as const;

// ============= SOCIAL MEDIA =============
export const SOCIAL_MEDIA = {
  FACEBOOK: 'https://facebook.com/toolsandbubbles',
  INSTAGRAM: 'https://instagram.com/toolsandbubbles',
  TWITTER: 'https://twitter.com/toolsandbubbles',
  LINKEDIN: 'https://linkedin.com/company/toolsandbubbles',
} as const;

// ============= SERVICE AREAS =============
export const SERVICE_AREAS = {
  KAKKANAD: [
    'InfoPark Kakkanad',
    'Seaport Airport Road',
    'Kakkanad HMT Colony',
    'Thrikkakara',
    'Chittethukara',
    'Kakkanad Junction',
    'Brahmapuram',
    'Vazhakkala',
    'Padamugal',
    'Muttom',
    'NGO Quarters',
    'Civil Station Road',
  ],
  EDAPPALLY: [
    'Edappally Metro Station',
    'Oberon Mall Area',
    'Changampuzha Park',
    'Rajagiri Hospital Area',
    'Ponekkara',
    'Edappally Junction',
    'Vennala',
    'NH Bypass Road',
    'Mamangalam',
    'Palarivattom',
    'JLN Stadium Area',
    'Kaloor',
  ],
} as const;
